/**
 * @fileoverview Utility functions for handling enum-like values
 * @description This file contains utility functions for handling enum-like values in TypeScript.
 * It provides type-safe utilities for checking if an enum-like value is included in an array or set of specific enum values.
 * It also provides a generic utility for picking specific values from a const assertion enum.
 */

/**
 * Type-safe utility for checking if an enum-like value is included in an array of specific enum values.
 * This function handles the type compatibility issues that arise when using const assertions for enums
 * and avoids the need for `as any` assertions throughout the codebase.
 *
 * @param enumArray - Array of specific enum values to check against
 * @param value - The enum value to check for inclusion
 * @returns A type predicate indicating whether the value is one of the array values
 *
 * @example
 * ```typescript
 * const ACTIVE_STATUSES = [OrdStatusEnum.New, OrdStatusEnum.PartiallyFilled] as const;
 *
 * // Instead of: ACTIVE_STATUSES.includes(order.OrdStatus as any)
 * if (includesEnumValue(ACTIVE_STATUSES, order.OrdStatus)) {
 *   // TypeScript now knows order.OrdStatus is one of the ACTIVE_STATUSES values
 * }
 * ```
 */
export function includesEnumValue<T extends readonly (string | number)[]>(
  enumArray: T,
  value: T[number]
): value is T[number];
export function includesEnumValue<T extends readonly (string | number)[]>(
  enumArray: T,
  value: string | number
): value is T[number];
export function includesEnumValue<T extends readonly (string | number)[]>(
  enumArray: T,
  value: string | number
): value is T[number] {
  return enumArray.includes(value as T[number]);
}

/**
 * Type-safe utility for checking if an enum-like value is included (i.e. has(value)) in a Set of specific enum values.
 * This function provides O(1) lookup performance with Sets while handling type compatibility issues
 * that arise when using const assertions for enums, avoiding the need for `as any` assertions.
 *
 * @param enumSet - Set of specific enum values to check against
 * @param value - The enum value to check for inclusion
 * @returns A type predicate indicating whether the value is one of the set values
 *
 * @example
 * ```typescript
 * const TERMINAL_STATUSES = [TransferStatusEnum.Completed, TransferStatusEnum.Rejected] as const;
 * const TERMINAL_STATUSES_SET = new Set(TERMINAL_STATUSES);
 *
 * // Instead of: TERMINAL_STATUSES_SET.has(transfer.Status as any)
 * if (includesEnumValueForSet(TERMINAL_STATUSES_SET, transfer.Status)) {
 *   // TypeScript now knows transfer.Status is one of the TERMINAL_STATUSES values
 *   // Plus you get O(1) lookup performance vs O(n) for arrays
 * }
 * ```
 */
export function includesEnumValueForSet<T extends string>(enumSet: Set<T>, value: T): value is T;
export function includesEnumValueForSet<T extends string>(enumSet: Set<T>, value: string): value is T;
export function includesEnumValueForSet<T extends string>(enumSet: Set<T>, value: string): value is T {
  return enumSet.has(value as T);
}
